import { useCallback, useEffect, useRef, useState } from "react"
import { motion, useReducedMotion } from "motion/react"

import { useAppState } from "../../context/appStateContext"
import native from "../../native/native"
import { formatCurrency } from "../../utils/utils"
import styles from "./CradlePaywall.module.scss"

/**
 * CradlePaywall (`/cradle`) — an original, self-contained FEATURE-SPECIFIC
 * paywall for Private Photo Vault Premium. Paywall type rolled at random this
 * run: FEATURE-SPECIFIC (one screen heroing the real "only your unlock" promise).
 *
 * Divergence brief + all axes: claude-paywalls/_ledger.md. Concept doc:
 * claude-paywalls/cradle.md.
 *
 * ── Style directive (read live from #paywalls each run) ─────────────────────
 * Newest @Paywalls-Bot mention this run: `focus on security and privacy` (NOT
 * the reset keyword `default`) → an ACTIVE STANDING DIRECTIVE at super-high
 * priority. The whole concept is built to it — but reframed away from the
 * saturated "who's attacking / where it leaks" angles into privacy as CUSTODY:
 * a memory held in your own hands and nobody else's.
 *
 * ── Concept ────────────────────────────────────────────────────────────────
 * "Some memories belong in your hands alone." Your most personal photos sit in a
 * warm, luminous cradle — two soft cupped arms that close around a cluster of
 * memories. In the open (arms apart) the memories are cool and exposed, ringed by
 * faint onlooker marks — everyone who could glance over. You "draw them close":
 * the two arms rotate inward and cup together, the onlookers fade, the held space
 * blooms warm, the tiles nestle and a small lock seats. The value is CUSTODY —
 * your memories are held, warm, in exactly one pair of hands: yours.
 *
 * ── Divergence (vs. the 90+ prior concepts, incl. the privacy cluster) ──────
 * Register    : TENDER / PROTECTIVE WARMTH — privacy as care, not defence. The
 *               whole cluster is solemn (Silvered), fearful (Caught/Sentry),
 *               machined (Reissue/Latent) or cool-analytic (Airtight/Cipher).
 * Hero motif  : CUPPED ARMS that CRADLE a warm memory cluster — no prior concept
 *               cradles. Not a door, dial, mirror, net or hub-and-spoke seal.
 * Interaction : DRAW THEM CLOSE — one tender gesture cups the arms shut; a
 *               collapse-into-care, not a lock-out or a reveal.
 * Art         : warm, light-first, soft (amber/rose held-warmth vs cool slate
 *               outside); brand blue kept as the you/key accent. Full dark too.
 * Value frame : CUSTODY + risk-reversal — "in your hands alone".
 *
 * ── Honest & App Store compliant ───────────────────────────────────────────
 * The cradle is a metaphor for REAL Premium features: only-you unlock (Face ID /
 * Touch ID / PIN + pattern lock), imported photos leave your Camera Roll & shared
 * albums, and a private encrypted Cloud Vault only you can open. No data-loss-on-
 * expiration claim, no fake urgency or scarcity, honest "Save %" from live product
 * data. Yearly is the featured, default, most prominent price; monthly is a small
 * secondary line. Bundled memories only — never the user's own library.
 */

type PlanKey = "yearly" | "monthly"

// Real personal-memory photos (bundled) for the cradled cluster. Never the
// user's own library — a demo set, shown in full colour.
const CLUSTER = ["m03", "m11", "m06", "m14", "m08"]

// Faint "onlooker" marks ringing the memories while they're in the open — the
// people who could glance over. Positioned as percentages of the stage box.
// They fade out the moment the memories are cradled. Purely decorative.
const ONLOOKERS = [
  { x: 12, y: 20 },
  { x: 88, y: 22 },
  { x: 6, y: 58 },
  { x: 94, y: 60 },
  { x: 24, y: 8 },
  { x: 76, y: 8 },
]

const CradlePaywall = () => {
  const { state } = useAppState()
  const reduceMotion = useReducedMotion()

  const premium = state.productData?.premium
  const yearlyProduct = premium?.yearlyProduct
  const monthlyProduct = premium?.monthlyProduct
  const locale = yearlyProduct?.priceLocale

  const yearlyPrice = yearlyProduct
    ? formatCurrency(yearlyProduct.price, locale)
    : "$35.99"
  const yearlyPerMonth = yearlyProduct
    ? formatCurrency(yearlyProduct.price / 12, locale)
    : "$2.99"
  const monthlyPrice = monthlyProduct
    ? formatCurrency(monthlyProduct.price, locale)
    : "$13.99"

  const savingsPct = (() => {
    const y = yearlyProduct?.price ?? 35.99
    const m = monthlyProduct?.price ?? 13.99
    if (!m) return 79
    return Math.round((1 - y / (m * 12)) * 100)
  })()

  const [held, setHeld] = useState<boolean>(!!reduceMotion)
  const [plan, setPlan] = useState<PlanKey>("yearly")
  const [showClose, setShowClose] = useState(false)
  const [hint, setHint] = useState(false)

  const touched = useRef(false)
  const timers = useRef<number[]>([])
  const pushTimer = (id: number) => timers.current.push(id)

  const cradle = useCallback(() => {
    setHeld(true)
    native.logMixpanelEvent("html_cradle_hold")
  }, [])

  // Auto-demo for passive viewers: let the memories sit exposed for a beat, then
  // draw them close so the payoff is always seen. Reduced motion starts cradled.
  useEffect(() => {
    if (reduceMotion) {
      setShowClose(true)
      return
    }
    pushTimer(window.setTimeout(() => setHint(true), 750))
    pushTimer(window.setTimeout(() => setShowClose(true), 2000))
    pushTimer(
      window.setTimeout(() => {
        if (!touched.current) cradle()
      }, 2900),
    )
    return () => {
      timers.current.forEach((id) => window.clearTimeout(id))
      timers.current = []
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleCradle = () => {
    touched.current = true
    setHint(false)
    cradle()
  }

  const selectedProduct = plan === "yearly" ? yearlyProduct : monthlyProduct
  const handleClose = () => native.postNativeEvent("close")
  const handleSubscribe = () => {
    native.logMixpanelEvent("html_cradle_subscribe", { plan })
    if (selectedProduct) native.subscribe(selectedProduct)
  }
  const openTerms = () => native.postNativeEvent("terms")
  const openPrivacy = () => native.postNativeEvent("privacy")

  return (
    <div className={`${styles.root} ${held ? styles.rootHeld : ""}`}>
      <div className={styles.aura} aria-hidden="true" />

      {/* ---------- header ---------- */}
      <header className={styles.top}>
        <div className={styles.brand}>
          <span className={styles.brandMark} aria-hidden="true">
            <svg viewBox="0 0 24 24" width="15" height="15">
              <path
                d="M12 2.6l6.6 2.5v5.2c0 4.3-2.8 7.7-6.6 9.1-3.8-1.4-6.6-4.8-6.6-9.1V5.1L12 2.6z"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.7"
                strokeLinejoin="round"
              />
              <circle cx="12" cy="11.4" r="2.1" fill="currentColor" />
            </svg>
          </span>
          <span className={styles.brandText}>
            Private Photo Vault
            <em>Premium</em>
          </span>
        </div>
        <button
          className={`${styles.close} ${showClose ? styles.closeOn : ""}`}
          onClick={handleClose}
          aria-label="Close"
          tabIndex={showClose ? 0 : -1}
        >
          <svg viewBox="0 0 24 24" width="14" height="14" aria-hidden="true">
            <path
              d="M5 5l14 14M19 5L5 19"
              stroke="currentColor"
              strokeWidth="2.2"
              strokeLinecap="round"
            />
          </svg>
        </button>
      </header>

      {/* ---------- title ---------- */}
      <div className={styles.intro}>
        <span className={styles.eyebrow}>Feature · Only your hands</span>
        <h1 className={styles.title}>
          Some memories belong in
          <br />
          <span className={styles.titleAccent}>your hands alone.</span>
        </h1>
        <p className={styles.sub}>
          Premium cradles your most personal photos where only you can reach
          them — held warm and clear for you, a sealed shell to everyone else.
        </p>
      </div>

      {/* ---------- the cradle stage ---------- */}
      <div className={styles.stage}>
        {/* faint onlookers ringing the open memories — everyone who could glance
            over; they fade the moment the memories are cradled */}
        {ONLOOKERS.map((o, i) => (
          <span
            key={i}
            className={styles.onlooker}
            style={
              {
                left: `${o.x}%`,
                top: `${o.y}%`,
                ["--d" as any]: `${i * 70}ms`,
              } as React.CSSProperties
            }
            aria-hidden="true"
          >
            <svg viewBox="0 0 24 24" width="18" height="18">
              <path
                d="M2 12s3.6-6.5 10-6.5S22 12 22 12s-3.6 6.5-10 6.5S2 12 2 12z"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.6"
              />
              <circle cx="12" cy="12" r="2.4" fill="currentColor" />
            </svg>
          </span>
        ))}

        {/* the two cupped arms — abstracted hands that close around the cluster */}
        <svg
          className={styles.arms}
          viewBox="0 0 100 100"
          preserveAspectRatio="xMidYMid meet"
          aria-hidden="true"
        >
          <defs>
            <linearGradient id="cradleArm" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="var(--arm-1)" />
              <stop offset="100%" stopColor="var(--arm-2)" />
            </linearGradient>
          </defs>
          <path
            className={`${styles.arm} ${styles.armL}`}
            d="M50 92 C24 92 12 74 12 54 C12 40 18 30 27 25"
            fill="none"
            stroke="url(#cradleArm)"
            strokeWidth="11"
            strokeLinecap="round"
          />
          <path
            className={`${styles.arm} ${styles.armR}`}
            d="M50 92 C76 92 88 74 88 54 C88 40 82 30 73 25"
            fill="none"
            stroke="url(#cradleArm)"
            strokeWidth="11"
            strokeLinecap="round"
          />
        </svg>

        {/* the held glow that blooms up between the arms once cradled */}
        <span className={styles.heldGlow} aria-hidden="true" />

        {/* the memory cluster nestled in the cradle */}
        <div
          className={`${styles.cluster} ${hint ? styles.clusterHint : ""}`}
          role="group"
          aria-label="Your memories"
        >
          {CLUSTER.map((m, i) => (
            <span
              key={m}
              className={styles.tile}
              data-i={i}
              style={{ transitionDelay: `${i * 45}ms` }}
            >
              <span
                className={styles.tileImg}
                style={{ backgroundImage: `url(/byheart/${m}.jpg)` }}
              />
            </span>
          ))}
          <span className={styles.clusterLock} aria-hidden="true">
            <svg viewBox="0 0 24 24" width="16" height="16">
              <path
                d={
                  held
                    ? "M7 10V8a5 5 0 0110 0v2M6 10h12v9H6z"
                    : "M7 10V8a5 5 0 019.5-2M6 10h12v9H6z"
                }
                fill="none"
                stroke="currentColor"
                strokeWidth="1.9"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </span>
        </div>
      </div>

      {/* readout */}
      <div className={styles.readout} aria-live="polite">
        <span
          className={`${styles.readoutDot} ${held ? styles.readoutDotOn : ""}`}
          aria-hidden="true"
        />
        <span className={styles.readoutText}>
          {held
            ? "Held close — in your hands, and no one else’s"
            : "Out in the open — anyone nearby could glance over"}
        </span>
      </div>

      {/* ---------- cradle control ---------- */}
      <div className={styles.control}>
        {held ? (
          <span className={styles.settled}>
            <svg viewBox="0 0 24 24" width="15" height="15" aria-hidden="true">
              <path
                d="M5 12.5l4 4 10-10.5"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.4"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            Cradled. Only your unlock opens them again.
          </span>
        ) : (
          <button className={styles.holdBtn} onClick={handleCradle}>
            <span className={styles.holdDot} aria-hidden="true" />
            Draw them close
          </button>
        )}
      </div>

      {/* ---------- proofs (real features) ---------- */}
      <div className={styles.proofs}>
        <span className={styles.proofsLead}>
          A warm feeling, made real by what Premium actually does:
        </span>
        <div className={styles.proofList}>
          {[
            "Only your unlock — Face ID, Touch ID, PIN or pattern",
            "Imported photos leave your Camera Roll & shared albums",
            "A private encrypted Cloud Vault only you can open",
          ].map((p, i) => (
            <span
              key={p}
              className={`${styles.proof} ${held ? styles.proofOn : ""}`}
              style={{ transitionDelay: `${160 + i * 90}ms` }}
            >
              <svg
                viewBox="0 0 24 24"
                width="13"
                height="13"
                aria-hidden="true"
              >
                <path
                  d="M5 12.5l4 4 10-10.5"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.6"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              {p}
            </span>
          ))}
        </div>
      </div>

      {/* ---------- pricing (yearly featured) ---------- */}
      <div className={styles.pricing}>
        <button
          className={`${styles.plan} ${styles.planYear} ${
            plan === "yearly" ? styles.planActive : ""
          }`}
          onClick={() => setPlan("yearly")}
          aria-pressed={plan === "yearly"}
        >
          <span className={styles.ribbon}>SAVE {savingsPct}%</span>
          <span className={styles.planLeft}>
            <span className={styles.radio} aria-hidden="true" />
            <span className={styles.planMeta}>
              <span className={styles.planName}>12 months</span>
              <span className={styles.planNote}>
                7 days free · then {yearlyPerMonth}/mo
              </span>
            </span>
          </span>
          <span className={styles.planRight}>
            <span className={styles.planPrice}>{yearlyPrice}</span>
            <span className={styles.planUnit}>/year</span>
          </span>
        </button>

        <button
          className={`${styles.plan} ${styles.planMonth} ${
            plan === "monthly" ? styles.planActive : ""
          }`}
          onClick={() => setPlan("monthly")}
          aria-pressed={plan === "monthly"}
        >
          <span className={styles.planLeft}>
            <span className={styles.radio} aria-hidden="true" />
            <span className={styles.planName}>Monthly</span>
          </span>
          <span className={styles.planMonthPrice}>{monthlyPrice}/mo</span>
        </button>
      </div>

      <motion.button
        className={styles.cta}
        onClick={handleSubscribe}
        whileTap={{ scale: 0.985 }}
      >
        <span className={styles.ctaMain}>
          {plan === "yearly" ? "Try 7 days free" : "Continue"}
        </span>
        <span className={styles.ctaSub}>
          {plan === "yearly"
            ? `Then ${yearlyPrice}/year · cancel anytime`
            : `${monthlyPrice}/month · cancel anytime`}
        </span>
      </motion.button>

      <div className={styles.legal}>
        <button onClick={openTerms}>Terms</button>
        <span aria-hidden="true">·</span>
        <button onClick={openPrivacy}>Privacy</button>
        <span aria-hidden="true">·</span>
        <span>Auto-renews until cancelled</span>
      </div>
    </div>
  )
}

export default CradlePaywall
